import React from 'react';
import { ExternalLink, Facebook, Instagram, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Brands = () => {
  const brands = [
    {
      id: 'tonymoly',
      name: 'TONYMOLY',
      tagline: 'Modern Korean Beauty',
      fullDescription: 'TONYMOLY is a globally recognized South Korean beauty brand known for its innovative skincare, high-quality formulations, and distinctive product design. Combining advanced technology with naturally inspired ingredients, the brand delivers effective solutions for a wide range of skin needs.',
      mongoliaDescription: 'In Mongolia, TONYMOLY brings modern K-beauty expertise to consumers through thoughtfully curated products that balance performance, creativity, and everyday skincare essentials.',
      image: '/images/tonymoly-brand.webp',
      website: 'https://tonymoly.mn/',
      facebook: 'https://www.facebook.com/TonyMolyMongolianDistributor',
      instagram: 'https://www.instagram.com/tonymoly.mn_official/',
      youtube: 'https://www.youtube.com/channel/UClcwJi4IJ8U3ImLqzeknZWQ',
      color: 'from-pink-600 to-rose-600'
    },
    {
      id: 'portmeirion',
      name: 'Portmeirion',
      tagline: 'British Elegance & Heritage',
      fullDescription: 'Portmeirion is a prestigious British home and lifestyle brand celebrated for its timeless design, craftsmanship, and heritage. Known worldwide for its iconic collections, the brand blends artistry with functionality to elevate everyday living.',
      mongoliaDescription: 'In Mongolia, Portmeirion offers elegant tableware and home pieces that reflect sophistication, tradition, and enduring quality.',
      image: '/images/portmeirion-brand.webp',
      website: 'https://portmeirion.mn/',
      facebook: 'https://www.facebook.com/PortmeirionMongolia/',
      instagram: 'https://www.instagram.com/portmeirion.mongolia/',
      youtube: 'https://www.youtube.com/channel/UC_5TRm3e2ooocwqJ3IldqGg',
      color: 'from-emerald-600 to-teal-600'
    },
    {
      id: 'ecover',
      name: 'Ecover',
      tagline: 'Clean Air & Sustainable Living',
      fullDescription: 'Ecover is a sustainability-driven brand focused on improving environmental quality and everyday well-being through clean air solutions. With an emphasis on innovation and eco-conscious technology, the brand promotes healthier indoor environments and responsible living.',
      mongoliaDescription: 'In Mongolia, Ecover provides advanced air purification solutions designed to enhance air quality and support a cleaner, healthier lifestyle.',
      image: '/images/ecover-brand.jpg',
      website: 'http://www.ecovermongolia.mn/',
      facebook: 'https://www.facebook.com/profile.php?id=61571940673538',
      instagram: 'https://www.instagram.com/ecover.cleanair/',
      youtube: null,
      color: 'from-blue-600 to-cyan-600'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-32 bg-black text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Our Brands
            </h1>
            <p className="text-2xl md:text-3xl text-gray-300 leading-relaxed">
              Curated excellence from around the world, bringing beauty, lifestyle, and wellness to Mongolia
            </p>
          </div>
        </div>
      </section>

      {/* Brand Sections */}
      {brands.map((brand, index) => (
        <section 
          key={brand.id} 
          id={brand.id}
          className={`py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="container mx-auto px-6 lg:px-12">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={brand.image}
                    alt={brand.name}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${brand.color} opacity-20`}></div>
                </div>
              </div>

              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div>
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {brand.tagline}
                  </p>
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                    {brand.name}
                  </h2>
                </div>

                <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                  <p>{brand.fullDescription}</p>
                  <p className="font-medium text-gray-900">{brand.mongoliaDescription}</p>
                </div>

                {/* Links */}
                <div className="pt-4 space-y-4">
                  <a 
                    href={brand.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-black text-white hover:bg-gray-800 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 w-full sm:w-auto">
                      Visit {brand.name} Mongolia
                      <ExternalLink className="ml-2 w-5 h-5" />
                    </Button>
                  </a>

                  {/* Social Links */}
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-600 font-medium">Follow:</span>
                    <a 
                      href={brand.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                      aria-label="Facebook"
                    >
                      <Facebook size={24} />
                    </a>
                    <a 
                      href={brand.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                      aria-label="Instagram"
                    >
                      <Instagram size={24} />
                    </a>
                    {brand.youtube && (
                      <a 
                        href={brand.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-black transition-colors p-2 hover:bg-gray-100 rounded-lg"
                        aria-label="YouTube"
                      >
                        <Youtube size={24} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Visit Our Retail Locations
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Experience our brands in person at 6 locations across Ulaanbaatar
          </p>
          <a href="/retail">
            <Button className="bg-white text-black hover:bg-gray-100 rounded-full px-10 py-7 text-xl font-semibold transition-all duration-300 hover:scale-105">
              Find a Store
              <ExternalLink className="ml-2 w-6 h-6" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Brands;
